'use client'

import type { ComponentType, SVGProps } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgBagShopping from '@/icons/regular/BagShopping'
import SvgBatteryBolt from '@/icons/regular/BatteryBolt'
import SvgBluetooth from '@/icons/regular/Bluetooth'
import SvgDroplet from '@/icons/regular/Droplet'
import SvgEar from '@/icons/regular/Ear'
import SvgHeadphones from '@/icons/regular/Headphones'
import SvgMagnet from '@/icons/regular/Magnet'
import SvgStethoscope from '@/icons/regular/Stethoscope'
import type { DirectoryRow } from '@/lib/directory/row'
import { tw } from '@/lib/utils'

type SpecIcon = ComponentType<SVGProps<SVGSVGElement>>

interface SpecChip {
  key: string
  label: string
  Icon: SpecIcon
}

// Buying-category icons follow the purchase panel's vocabulary.
const classificationIcon = (value: string): SpecIcon => {
  if (value === 'OTC') {
    return SvgBagShopping
  }
  if (value === 'Hearable') {
    return SvgHeadphones
  }
  return SvgStethoscope
}

interface RowSpecsProps {
  release: Pick<DirectoryRow, 'cardSlug' | 'products'>
  facetLabels: Map<string, string>
}

// The facts a shopper filters by, on the row itself as icon chips — for the
// model the row pictures (the card product), not the family: its style, its
// type, then its own quick-glance specs, so a CIC without a telecoil is not
// dressed in its RIC sibling's chips. Brand names, "Bluetooth" and IP codes
// are not translated.
export const RowSpecs = ({ release, facetLabels }: RowSpecsProps) => {
  const { t } = useTranslation()
  const card = release.products.find((product) => product.slug === release.cardSlug) ?? release.products[0]
  if (!card) {
    return null
  }
  const chips: SpecChip[] = []
  if (card.style) {
    chips.push({ key: 'style', label: facetLabels.get(card.style) ?? card.style, Icon: SvgEar })
  }
  if (card.classification) {
    chips.push({
      key: 'type',
      label: facetLabels.get(card.classification) ?? card.classification,
      Icon: classificationIcon(card.classification),
    })
  }
  if (card.specs.rechargeable) {
    chips.push({ key: 'rechargeable', label: t('directory.spec.rechargeable', 'Rechargeable'), Icon: SvgBatteryBolt })
  }
  if (card.specs.bluetooth) {
    chips.push({ key: 'bluetooth', label: 'Bluetooth', Icon: SvgBluetooth })
  }
  if (card.specs.telecoil) {
    chips.push({ key: 'telecoil', label: t('directory.spec.telecoil', 'Telecoil'), Icon: SvgMagnet })
  }
  if (card.specs.ip) {
    chips.push({ key: 'ip', label: card.specs.ip, Icon: SvgDroplet })
  }

  if (chips.length === 0) {
    return null
  }

  // Phones show the first four (style, type and the two most-asked specs);
  // wider screens show them all.
  return (
    <ul className="flex flex-wrap gap-1.5 md:gap-2">
      {chips.map(({ key, label, Icon }, index) => (
        <li
          key={key}
          className={tw(
            'border-lapis/30 bg-lapis/5 text-navy/80 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] leading-none font-semibold',
            index >= 4 && 'hidden sm:inline-flex'
          )}
        >
          <Icon aria-hidden className="fill-lapis h-3.5 w-3.5 shrink-0" />
          {label}
        </li>
      ))}
    </ul>
  )
}
